<script lang="ts">
	import { onMount, onDestroy, tick } from 'svelte';
	import type { ComponentType } from 'svelte';
	import ComponentPageHead from '$lib/components/ComponentPageHead.svelte';
	import RebrandLayoutSkeleton from '$lib/components/skeletons/RebrandLayoutSkeleton.svelte';
	import {
		markVariantReady,
		markVariantPainted,
		afterPaint,
		instrument,
	} from '$lib/services/sentry';

	// Funnel-route shell: optional skeleton until `page.init()` resolves, then variant + paint marks.
	// `page.paint` stays open across `tick()` + `afterPaint` so the idle transaction cannot
	// finish before variant-painted / payment-form-interactive. Destroyed-guards after every
	// await; `cancelPaint` closes the span on unmount instead of hanging to childSpanTimeout.

	/** Structural shape of the `page` object produced by `$lib/utils/page`. */
	type PageLike = {
		init(): Promise<ComponentType | undefined>;
		metadata?: Record<string, unknown>;
	};

	/** Route load data: `page` (Page instance) plus optional initial `metadata`. */
	export let data: { page?: PageLike; metadata?: Record<string, unknown> };
	/** Stable route key stamped on the skeleton's `data-skeleton` marker. */
	export let routeKey: string;
	/** Optional async work awaited BEFORE `page.init()` (omit when none). */
	export let preInit: (() => Promise<unknown>) | undefined = undefined;
	/** When true, show RebrandLayoutSkeleton until `page.init()` resolves. */
	export let showSkeleton = false;

	const { page } = data;
	let metadata = data.metadata;
	let Component: ComponentType | undefined;
	let destroyed = false;
	let cancelPaint: () => void = () => {};

	onMount(async () => {
		if (preInit) {
			await instrument('page.preinit', 'Pre-init', () => preInit());
			if (destroyed) return;
		}
		const resolved = await page?.init();
		if (destroyed) return;
		if (page) metadata = { ...page.metadata };
		if (resolved) {
			Component = resolved;
			markVariantReady();
			await instrument('page.paint', 'Variant paint', async () => {
				await tick();
				if (destroyed) return;
				await new Promise<void>((resolve) => {
					const cancelAfterPaint = afterPaint(() => {
						markVariantPainted();
						resolve();
					});
					cancelPaint = () => {
						cancelAfterPaint();
						resolve();
					};
				});
			});
		}
	});

	onDestroy(() => {
		destroyed = true;
		cancelPaint();
	});
</script>

{#if metadata}
	{#key metadata}
		<ComponentPageHead content={metadata} />
	{/key}
{/if}

{#if Component}
	<svelte:component
		this={Component}
		pageData={data}
	/>
{:else if showSkeleton && $$slots.default}
	<RebrandLayoutSkeleton {routeKey}>
		<slot />
	</RebrandLayoutSkeleton>
{:else if showSkeleton}
	<RebrandLayoutSkeleton {routeKey} />
{/if}
